import { useState } from 'react';
import { Clock, X } from 'lucide-react';
import { getDevTodayOverride, setDevTodayOverride, isDevHost } from '../../lib/devClock';
import { todayYMD } from '../../lib/dateOnly';

/**
 * Dev-only floating control for the simulated "today" (see lib/devClock.ts).
 * Renders only on dev hosts. Collapsed it's a small pill — amber while a
 * simulated date is active, subtle otherwise. Open it to pick a date, apply it,
 * or reset to the real today. Applying reloads so every date-driven view
 * (the CFP timeline especially) recomputes against the chosen day.
 */
export default function DevDateBadge() {
  if (!isDevHost()) return null;

  const active = getDevTodayOverride();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(() => active ?? todayYMD());

  const apply = () => {
    setDevTodayOverride(value || null);
    window.location.reload();
  };
  const reset = () => {
    setDevTodayOverride(null);
    window.location.reload();
  };

  return (
    <div className="fixed bottom-3 right-3 z-[60] text-xs">
      {open ? (
        <div className="w-60 rounded-xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 font-semibold text-gray-900 dark:text-gray-100">
              <Clock className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" /> Simulated date (dev)
            </span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mb-2 text-gray-500 dark:text-gray-400">
            Pretend it's another day to test date-driven UI. Dev only — never affects production.
          </p>
          <label htmlFor="dev-sim-date" className="sr-only">Simulated date</label>
          <input
            id="dev-sim-date"
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mb-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1.5 text-gray-900 dark:text-gray-100"
          />
          <div className="flex gap-2">
            <button type="button" onClick={apply} className="flex-1 rounded-lg bg-amber-500 px-2 py-1.5 font-semibold text-white hover:bg-amber-600">
              Apply
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={!active}
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-2 py-1.5 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
            >
              Reset to today
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Simulated date (dev tool)"
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold shadow-lg ${
            active ? 'bg-amber-500 text-white' : 'bg-gray-800/80 text-gray-100 hover:bg-gray-700'
          }`}
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          {active ? `Simulated: ${active}` : 'Sim date'}
        </button>
      )}
    </div>
  );
}
